import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Tv as TvIcon
} from '@mui/icons-material';
import api from '../services/api';

const TVGuidePage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url_template: '',
    is_active: true,
    priority: 0,
    timeout: 10
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/tv-guide-sources');
      setSources(response.data);
    } catch (err) {
      setError('Ошибка загрузки источников телепрограммы');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (source = null) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        name: source.name,
        url_template: source.url_template,
        is_active: source.is_active,
        priority: source.priority,
        timeout: source.timeout
      });
    } else {
      setEditingSource(null);
      setFormData({
        name: '',
        url_template: '',
        is_active: true,
        priority: 0,
        timeout: 10
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSource(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editingSource) {
        await api.put(`/admin/tv-guide-sources/${editingSource.id}`, formData);
        setSuccess('Источник успешно обновлен');
      } else {
        await api.post('/admin/tv-guide-sources', formData);
        setSuccess('Источник успешно добавлен');
      }
      handleCloseDialog();
      loadSources();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при сохранении источника');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот источник?')) {
      return;
    }

    try {
      await api.delete(`/admin/tv-guide-sources/${id}`);
      setSuccess('Источник успешно удален');
      loadSources();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Ошибка при удалении источника');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <TvIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Телепрограмма
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Управление источниками телепрограммы для проксирования
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Добавить источник
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>URL шаблон</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Приоритет</TableCell>
              <TableCell>Таймаут (сек)</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Нет источников. Добавьте первый источник телепрограммы.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sources.map((source) => (
                <TableRow key={source.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {source.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {source.url_template}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={source.is_active ? 'Активен' : 'Неактивен'}
                      color={source.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {source.priority}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {source.timeout} сек
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(source)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(source.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог добавления/редактирования источника */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSource ? 'Редактировать источник' : 'Добавить источник'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Название источника"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Например: GlazTV"
              required
            />

            <TextField
              fullWidth
              label="URL шаблон"
              value={formData.url_template}
              onChange={(e) => handleInputChange('url_template', e.target.value)}
              placeholder="http://example.com/channels/{channel}.json"
              helperText="Используйте {channel} как плейсхолдер для названия канала"
              required
            />

            <TextField
              fullWidth
              type="number"
              label="Приоритет"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 0)}
              helperText="Больше значение = выше приоритет (источники с высоким приоритетом проверяются первыми)"
            />

            <TextField
              fullWidth
              type="number"
              label="Таймаут (секунды)"
              value={formData.timeout}
              onChange={(e) => handleInputChange('timeout', parseInt(e.target.value) || 10)}
              helperText="Максимальное время ожидания ответа от источника"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                />
              }
              label="Источник активен"
            />

            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.url_template}
          >
            {editingSource ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TVGuidePage;
